import { Inertia } from '@inertiajs/inertia';
import { settleReveal } from './reveal';

var bound = false;
var hashVisitQueued = false;
var pendingHashPath = '';
var pendingHashId = '';
var tween = {
  frame: 0,
  startY: 0,
  targetY: 0,
  distance: 0,
  startTime: 0,
  duration: 0
};

/* The curve and the duration of a scroll, written once.

   These were private to the document's tween, which was the only scroller on
   the site that had one. They are exported because the chat log is a scroller
   of its own: it is an overflow container inside the page, so it cannot go
   through `scrollToY`, which is `window`, but it still has to move the way
   every other scroll on the site moves. `_reset.scss` holds
   `scroll-behavior: auto` on the root deliberately, so the browser's own smooth
   scroll is not an option here and this curve is what "smooth" means. */
export function scrollEase(t) {
  return 1 - Math.pow(1 - t, 3);
}

export function scrollDuration(distance) {
  var px = Math.abs(distance);
  return Math.max(140, Math.min(450, px * 0.32));
}

function setScrollY(y) {
  y = Math.max(0, y);
  window.scrollTo({
    top: y,
    left: 0,
    behavior: 'auto'
  });
}

function currentY() {
  return window.pageYOffset || document.documentElement.scrollTop || 0;
}

function setLocation(url) {
  if (!window.history || !window.history.replaceState) {
    return;
  }

  window.history.replaceState(window.history.state, '', url);
}

export function cancelScroll() {
  if (tween.frame) {
    window.cancelAnimationFrame(tween.frame);
    tween.frame = 0;
  }

  tween.startTime = 0;
}

function step(timestamp) {
  if (!tween.startTime) {
    tween.startTime = timestamp;
  }

  var t = Math.min(1, (timestamp - tween.startTime) / tween.duration);
  setScrollY(tween.startY + tween.distance * scrollEase(t));

  if (t < 1) {
    tween.frame = window.requestAnimationFrame(step);
    return;
  }

  tween.frame = 0;
  tween.startTime = 0;
  setScrollY(tween.targetY);
}

export function scrollToY(targetY, duration) {
  targetY = Math.max(0, targetY);
  var startY = currentY();
  var distance = targetY - startY;

  if (Math.abs(distance) < 1) {
    cancelScroll();
    setScrollY(targetY);
    return;
  }

  if (tween.frame && Math.abs(tween.targetY - targetY) < 2) {
    return;
  }

  tween.startY = startY;
  tween.targetY = targetY;
  tween.distance = distance;
  tween.startTime = 0;
  tween.duration = typeof duration === 'number' ? duration : scrollDuration(distance);

  if (!tween.frame) {
    tween.frame = window.requestAnimationFrame(step);
  }
}

export function scrollToId(id) {
  var el = document.getElementById(id);
  if (!el) {
    return false;
  }

  /* A block the scroll reveal has not reached yet is painted below its layout
     position, so it has to be settled before it is measured or the jump lands
     short by exactly that distance. See reveal.js. */
  settleReveal(el);

  scrollToY(el.getBoundingClientRect().top + currentY());
  return true;
}

export function scrollToTop() {
  scrollToY(0);
}

/* An anchor that belongs to another page (a link like /web-design#one-off-plans
   clicked from the home page) has to visit that page before the hash exists to
   scroll to. The target path is remembered alongside the id, so the hash lands
   on the page that owns it rather than always falling back to the home page. */
function visitPageHash(path, id) {
  pendingHashPath = path;
  pendingHashId = id;

  if (hashVisitQueued) {
    return;
  }

  hashVisitQueued = true;

  Inertia.visit(path, {
    onFinish: function () {
      hashVisitQueued = false;
    },
    onSuccess: function () {
      var targetPath = pendingHashPath;
      var targetId = pendingHashId;
      pendingHashPath = '';
      pendingHashId = '';

      window.setTimeout(function () {
        if (!targetId) {
          return;
        }

        scrollToId(targetId);
        setLocation(targetPath + '#' + targetId);
      }, 80);
    }
  });
}

export function goToHash(id, path) {
  if (!id) {
    return;
  }

  var currentPath = window.location.pathname || '/';
  var targetPath = path || currentPath;

  /* Scroll here when this page already has the section. A root hash such as
     `/#cta` used to leave for home first, even though the enquiry band is on
     every public page. A link to a different path is visited only when the
     section is not on this page, so the hash is resolved against the page
     that owns it. */
  if (targetPath === currentPath || document.getElementById(id)) {
    if (scrollToId(id)) {
      var next = currentPath + '#' + id;
      if (currentPath + window.location.hash !== next) {
        setLocation(next);
      }
    }

    return;
  }

  visitPageHash(targetPath, id);
}

export function bindHashLinks() {
  if (bound) {
    return;
  }

  bound = true;

  Inertia.on('start', cancelScroll);

  document.addEventListener(
    'click',
    function (event) {
      var link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      /* An anchor that names its own tab is left alone. The chat's generated
         replies mark their external links `target="_blank"`, and a visitor who
         wrote the target themselves has the same expectation: that click is
         already decided, and turning it into a same-tab Inertia visit would be
         answering a question nobody asked. */
      if (link.target && link.target !== '_self') {
        return;
      }

      var href = link.getAttribute('href');
      if (!href || href === '#') {
        return;
      }

      var url;
      try {
        url = new URL(href, window.location.href);
      } catch (err) {
        return;
      }

      if (url.origin !== window.location.origin || !url.hash || url.hash.length < 2) {
        return;
      }

      event.preventDefault();
      goToHash(decodeURIComponent(url.hash.slice(1)), url.pathname);
    },
    true
  );
}
